import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { getMwstCode, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    addAngebot,
    Angebot,
    copyAngebot,
    deleteAngebot,
    getDefaultAngebot,
    recalculateAngebot,
    sendAngebot,
    updateAngebot,
    useAngebot,
} from 'api/angebot';
import { useHistory } from 'react-router-dom';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { AngebotStatus } from 'formComponents/lookups/AngebotStatus';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, createRecordUrl, formatMoney } from 'utils/formatter';
import { AngebotPosition, useAngebotPositionList } from 'api/angebotPosition';
import { AnsprechpartnerSelect } from 'components/Kunde/Ansprechpartner/AnsprechpartnerSelect';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { getApiUrl } from 'api/useApi';
import { addAuftragFromAngebot } from 'api/auftrag';
import { SendMail } from 'components/SendMail/SendMail';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { AngebotStatusLabel } from 'utils/labels';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { logDifference } from 'utils/helper';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { SmRefType } from 'api/attachment';
import { AraRegelung } from 'formComponents/lookups/AraRegelung';
import { useTerminList } from 'api/termin';
import { AblehnGrund } from 'formComponents/lookups/AblehnGrund';
import { useSelector } from 'react-redux';
import { AngebotPositionen } from './AngebotPositionen';
import { AngebotPositionenSelect } from './AngebotPositionenSelect';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';
import { AddAttachments } from '../History/AddAttachments';
import { addServiceFromAngebot } from '../../api/service';
import { getArtikelDetail } from '../../api/artikelDetail';
import useArtikelCalculator from '../../hooks/useArtikelCalculator';
import { authSelector } from '../../store/auth/selector';

export interface AngebotEditData {
    kNr?: number;
    aNr: string;
}

interface AngebotEditProps {
    defaultKNr?: number;
    aNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (aNr: string) => void;
}

type ButtonAction =
    | 'reopen'
    | 'print'
    | 'printOnBriefpapier'
    | 'send'
    | 'copy'
    | 'createAuftrag'
    | 'createService'
    | 'delete';

const allowedStatus = [0, 1, 2, 3];

const styles = mergeStyleSets({
    confirmContainer: {
        minHeight: 300,
    },
    ablehnGrund: {
        marginTop: 20,
    },
});

export const AngebotEdit: React.FC<AngebotEditProps> = ({ defaultKNr, aNr, onClose, onReopen }) => {
    const auth = useSelector(authSelector);
    const history = useHistory();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [ablehnGrund, setAblehnGrund] = React.useState(0);

    const [angebot, setAngebot] = React.useState<Angebot | null>(getDefaultAngebot(defaultKNr));
    const dbAngebot = useAngebot(
        {
            nr: aNr,
        },
        {
            enabled: aNr !== 'neu',
            onSuccess: (loadedAngebot) => {
                setAngebot(cloneDeep(loadedAngebot));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: angebot?.kNr,
            withZusatzadresseList: true,
            withAttachmentList: true,
        },
        {
            enabled: angebot?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (aNr === 'neu' && angebot) {
                    setAngebot({
                        ...angebot,
                        betreuer: newKunde?.betreuer || 0,
                        telBetreuer: newKunde?.telBetreuer || 0,
                        zahlBed: newKunde?.zahlZiel || angebot.zahlBed,
                        mwstCode: getMwstCode(newKunde),
                        ara: newKunde?.ara || 0,
                    });
                }
            },
        },
    );

    const angebotPositionList = useAngebotPositionList(
        {
            aNr,
        },
        { enabled: aNr !== 'neu' },
    );

    const kNrArtikelGruppeNrAndArtikelNrList = React.useMemo(() => {
        const list: string[] = [];
        if (angebot && angebotPositionList.data) {
            (angebotPositionList.data || []).forEach((p) => {
                if (p.artikelGruppeNr && p.artikelNr) {
                    list.push(angebot.kNr + '|' + p.artikelGruppeNr + '|' + p.artikelNr);
                }
            });
        }

        return list;
    }, [angebot, angebotPositionList]);

    const wiedervorlagen = useTerminList({
        terminStatus: 0,
        terminArt: 'W',
        kNrArtikelGruppeNrAndArtikelNrList,
    });

    const [showCopy, setShowCopy] = React.useState(false);
    const [showSend, setShowSend] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [showCreateAuftrag, setShowCreateAuftrag] = React.useState(false);
    const [showCreateService, setShowCreateService] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    const [transferPositionen, setTransferPositionen] = React.useState<AngebotPosition[] | null>(null);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    const artikelCalculator = useArtikelCalculator();

    /*
    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kundeList.data[0].nr !== kunde?.nr
        ) {
            if (aNr === 'neu' && angebot) {
                setAngebot({
                    ...angebot,
                    betreuer: kundeList.data[0]?.betreuer || 0,
                    telBetreuer: kundeList?.data[0]?.telBetreuer || 0,
                    zahlBed: kundeList?.data[0]?.zahlZiel || angebot.zahlBed,
                    mwstCode: getMwstCode(kundeList.data[0]),
                    ara: kundeList.data[0]?.ara || 0,
                });
            }
        }
    }, [kunde, kundeList, aNr, angebot]);
     */

    /*
    React.useEffect(() => {
        if (!angebotList.isLoading && !angebotList.isFetching && angebot === null) {
            if (angebotList.data?.length === 1) {
                setAngebot(cloneDeep(angebotList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setAngebot(getDefaultAngebot(defaultKNr));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [angebot, angebotList, defaultKNr, auth]);
     */

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbAngebot.isLoading &&
            !dbAngebot.isFetching &&
            angebot !== null
        ) {
            if (!isEqual(dbAngebot.data, angebot)) {
                setSavingState(SavingState.notSaved);
                logDifference('angebot', angebot, dbAngebot.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbAngebot, angebot]);

    const readOnly = React.useMemo(() => {
        return angebot?.status !== 0;
    }, [angebot?.status]);

    const validate = React.useCallback((): boolean => {
        if (angebot?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Kunden aus.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [angebot, validationError]);

    const getDistinctAngebotPositionen = React.useCallback(() => {
        const distinctAngebotPositionen: AngebotPosition[] = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const angebotPosition of angebotPositionList?.data || []) {
            const existing = distinctAngebotPositionen.find(
                (dap) =>
                    dap.artikelGruppeNr === angebotPosition.artikelGruppeNr &&
                    dap.artikelNr === angebotPosition.artikelNr,
            );
            if (existing) {
                existing.posMenge += angebotPosition.posMenge;
            } else {
                distinctAngebotPositionen.push({ ...angebotPosition });
            }
        }

        return distinctAngebotPositionen;
    }, [angebotPositionList?.data]);

    const handleButtonAction = React.useCallback(
        async (action?: ButtonAction) => {
            if (!angebot?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(angebot.nr);
                    break;
                case 'print':
                    window.open(getApiUrl('angebot/pdf') + '?nr=' + angebot.nr);
                    break;
                case 'printOnBriefpapier':
                    window.open(getApiUrl('angebot/pdf') + '?nr=' + angebot.nr + '&briefpapier=true');
                    break;
                case 'send':
                    setShowSend(true);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                case 'createAuftrag':
                    setTransferPositionen(
                        (angebotPositionList?.data || []).map((ap) => ({
                            ...ap,
                            auNr: '',
                            offen: 0,
                            transfer: true,
                            transferMenge: ap.posMenge,
                            lArt: '',
                        })),
                    );
                    setShowCreateAuftrag(true);
                    break;
                case 'createService':
                    {
                        const newTransferPositionen = await Promise.all(
                            getDistinctAngebotPositionen().map(async (ap) => {
                                const artikelDetail = await getArtikelDetail({
                                    artikelGruppeNr: ap.artikelGruppeNr,
                                    artikelNr: ap.artikelNr,
                                });

                                return {
                                    ...ap,
                                    transfer: false,
                                    transferMenge: ap.posMenge,
                                    transferBestand: artikelDetail.bestand,
                                };
                            }),
                        );

                        setTransferPositionen(newTransferPositionen);
                        setShowCreateService(true);
                    }
                    break;
                default:
                    break;
            }
        },
        [angebot, onReopen, angebotPositionList?.data, getDistinctAngebotPositionen],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number, additionalChanges?: Partial<Angebot>) => {
            if (!angebot) {
                return;
            }

            const updatedAngebot: Angebot =
                status === undefined ? angebot : { ...angebot, status, ...(additionalChanges || {}) };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (aNr === 'neu') {
                        addAngebot(updatedAngebot)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateAngebot(updatedAngebot)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbAngebot.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(angebot.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [dbAngebot, angebot, aNr, isMounted, onReopen, savingState, validate],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: AngebotStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [
            {
                key: 'print',
                text: 'Drucken mit Briefkopf',
                iconProps: { iconName: 'Print' },
                onClick: () => (handleButtonAction('print').then() as never) as void,
            },
        ];

        if (aNr !== 'neu' && angebot?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => (handleButtonAction('delete').then() as never) as void,
            });
        }

        return { items };
    }, [aNr, angebot?.status, handleButtonAction]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Angebot, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(angebot?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Angebot, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={angebot?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof Angebot, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideFormAligned}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={angebot?.[column] as boolean}
            onChange={(event, checked) => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderMa = (column: keyof Angebot, label: string) => (
        <BetreuerById
            value={angebot?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty
            menuPlacement="top"
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        [column]: newValue,
                    });
                }
            }}
        />
    );

    const renderTextbaustein = (
        column: keyof Angebot,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={angebot?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (column: keyof Angebot, label: string, required = false, defaultNow = false) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(angebot?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (angebot) {
                        setAngebot({
                            ...angebot,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: readOnly,
                }}
            />
        );
    };

    const renderAngebotStatus = () => (
        <AngebotStatus
            value={angebot?.status?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderAblehnGrund = () => (
        <AblehnGrund
            value={angebot?.ablehnGrund?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        ablehnGrund: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            label="Kunde"
            kNr={angebot?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly /* || aNr !== 'neu' */}
            onChange={(newKNr): void => {
                if (angebot && angebot.kNr !== newKNr) {
                    setAngebot({
                        ...angebot,
                        kNr: newKNr,
                        kaNr: 0,
                        kaName: '',
                    });
                }
            }}
        />
    );

    const renderAnsprechpartner = () => (
        <AnsprechpartnerSelect
            label="Ansprechpartner"
            kNr={angebot?.kNr}
            kaNr={angebot?.kaNr}
            kaName={angebot?.kaName}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newKaNr, newKaName): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        kaNr: newKaNr || 0,
                        kaName: newKaName || '',
                    });
                }
            }}
        />
    );

    const renderSkontodefinition = () => (
        <Skontodefinition
            value={angebot?.zahlBed || 0}
            label="Zahlungsbedingungen"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        zahlBed: newValue,
                    });
                }
            }}
        />
    );

    const renderAraRegelung = () => (
        <AraRegelung
            value={angebot?.ara || 0}
            label="ARA Regelung"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (angebot) {
                    setAngebot({
                        ...angebot,
                        ara: newValue,
                    });
                }
            }}
        />
    );

    /*
    const validationWarning =
        angebot?.ara !== 1 && // "Die Ware ist nicht entpflichtet"
        angebotPositionList.data &&
        !angebotPositionList.data.find((ap) => ap.artikelGruppeNr === 97)
            ? 'Achtung: Es ist noch kein Reclay Artikel eingetragen.'
            : null;
     */

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={`Angebot ${angebot?.nr || ''} - ${kunde.data?.name1 || '...'} ${kunde.data?.name2 || ''} ${
                    kunde.data?.nr ? '(' + kunde.data?.nr + ')' : ''
                }`}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde.data ? <OpenLinkIcon link={createKundeUrl(kunde.data.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell}>{renderAnsprechpartner()}</div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Angebot-Nr.', { maxLength: 10, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('auNr', 'Auftrag-Nr.', { maxLength: 10 })}
                                    {angebot?.auNr ? (
                                        <OpenLinkIcon link={createRecordUrl('auftraege', defaultKNr, angebot?.auNr)} />
                                    ) : null}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Ausstellungsdatum', true, true)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datBis', 'Angebot gültig bis')}
                                </div>
                                <div className={formStyles.gridCell}>{renderMa('betreuer', 'Betreuer')}</div>
                                <div className={formStyles.gridCell}>{renderMa('telBetreuer', 'Tel.-Betreuer')}</div>
                            </div>
                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && aNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.angebot}
                                        smRef={aNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <div className={formStyles.gridCell}>{renderTextarea('txt', 'Text')}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB1', 'Textbaustein 1', 21, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB2', 'Textbaustein 2', 21, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB3', 'Textbaustein 3', 21, true, 'top')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB4', 'Textbaustein 4', 21, true, 'top')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB5', 'Textbaustein 5', 21, true, 'top')}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderAngebotStatus()}</div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderAblehnGrund()}</div>
                                <div className={formStyles.gridCellRight}>
                                    {renderCheckbox('extDoku', 'Ext. Doku.')}
                                </div>
                                {/*
                                <div className={formStyles.gridCell}>{renderCheckbox('teilR', 'Teilrechnung')}</div>
                                */}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefBed', 'Lieferbedingung', 11, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefZeit', 'Lieferzeit', 14, true)}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderDatePicker('liefDat', 'Lieferdatum')}</div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('liefKw', 'Liefer-KW', { numeric: true })}
                                </div>
                            </div>
                            <div className={formStyles.gridCell}>
                                {/* renderTextbaustein('zahlBed', 'Zahlungsbedingungen', 12, true, 'top') */}
                                {renderSkontodefinition()}
                            </div>
                            <div className={formStyles.gridCell}>{renderAraRegelung()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('aktText', 'Betreff E-Akt', { maxLength: 100 })}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell} />
                                <div className={formStyles.gridCell}>
                                    {renderTextField('mwstCode', 'MwSt.', { numeric: true, disabled: true })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Kunden)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Kunden)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                        <div className={formStyles.secondColumn}>{renderTextarea('zusatz', 'Zusatzvereinbarung')}</div>
                    </div>

                    <div>
                        {angebot && angebotPositionList?.data && kunde.data && (
                            <AngebotPositionen
                                kunde={kunde.data}
                                angebot={angebot}
                                aNr={angebot.nr}
                                angebotPositionen={angebotPositionList?.data}
                                wiedervorlagen={wiedervorlagen.data || []}
                                viewMode="long"
                                allowEdit={!readOnly && savingState === SavingState.notChanged}
                                refetch={async () => {
                                    const result = await recalculateAngebot(angebot);
                                    if (result?.angebot) {
                                        dbAngebot.refetch().then();
                                        setAngebot({
                                            ...angebot,
                                            sNetto: result.angebot.sNetto,
                                            sBrutto: result.angebot.sBrutto,
                                            sMwst: result.angebot.sMwst,
                                        });
                                        angebotPositionList.refetch().then();
                                        wiedervorlagen.refetch().then();
                                    }

                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {angebotPositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(angebot?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(angebot?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(angebot?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={angebot?.anlageDat}
                        createdDecimalTime={angebot?.anlageUhr}
                        createdUser={angebot?.anlageUser}
                        modifiedDate={angebot?.updDat}
                        modifiedDecimalTime={angebot?.updUhr}
                        modifiedUser={angebot?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {/* validationWarning && <div className={formStyles.warn}>{validationWarning}</div> */}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !angebot?.nr || auth.rolle === 'EX'}
                        />
                        <DefaultButton
                            className={formStyles.sendButton}
                            onClick={() => handleButtonAction('send')}
                            text="Senden"
                            disabled={savingState !== SavingState.notChanged || !angebot?.nr || auth.rolle === 'EX'}
                        />
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => handleButtonAction('createAuftrag')}
                            text="Auftrag erstellen"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                !angebot?.nr ||
                                !(angebot?.status === 0 || angebot?.status === 1) ||
                                auth.rolle === 'EX'
                            }
                        />
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => handleButtonAction('createService')}
                            text="Service erstellen"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                !angebot?.nr ||
                                !(angebot?.status === 0 || angebot?.status === 1) ||
                                auth.rolle === 'EX'
                            }
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !angebot?.nr || auth.rolle === 'EX'}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !angebot?.nr || auth.rolle === 'EX'}
                            onClick={() => handleButtonAction('printOnBriefpapier')}
                            text="Drucken"
                            menuProps={actionButtonMenuProps}
                        />

                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={
                                    savingState === SavingState.notChanged || dbAngebot.isLoading || auth.rolle === 'EX'
                                }
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbAngebot.isLoading || dbAngebot.isFetching || kunde.isLoading || kunde.isFetching || !angebot) && (
                    <LoadingIndicator />
                )}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Kunde"
                    disabled={savingState === SavingState.saving || readOnly || aNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={angebot?.kNr}
                    onChange={async (newKNr) => {
                        if (angebot && newKNr) {
                            const { newNr } = await copyAngebot(newKNr, angebot, angebotPositionList?.data);
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showSend && (
                <SendMail
                    kNr={angebot?.kNr || 0}
                    kaNr={angebot?.kaNr}
                    markAsSentDisabled={angebot?.status !== 0}
                    markAsSentLabel="Angebot als versendet markieren"
                    onSubmit={(kaNrListTo, kaNrListCC, markAsSent) => {
                        if (angebot) {
                            sendAngebot(angebot, kaNrListTo, kaNrListCC).then(() => {
                                setShowSend(false);
                                if (markAsSent && angebot) {
                                    save(undefined, 1);
                                }
                            });
                        }
                    }}
                    onCancel={() => setShowSend(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Angebot löschen"
                    message={'Soll das Angebot ' + angebot?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (angebot?.nr) {
                            deleteAngebot(angebot).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => setShowDelete(false)}
                />
            )}
            {showCreateAuftrag && (
                <ConfirmMessage
                    title="Auftrag erstellen"
                    message={'Soll aus dem Angebot ' + angebot?.nr + ' ein Auftrag erstellt werden?'}
                    minWidth={1000}
                    content={
                        <>
                            <ul>
                                <li>Ein Auftrag mit den gewählten Positionen aus dem Angebot wird erstellt.</li>
                                <li>Die neue Auftragsnummer wird beim Angebot hinterlegt.</li>
                                <li>
                                    Der Status des Angebotes wird auf <strong>{AngebotStatusLabel[2]}</strong> gesetzt.
                                </li>
                                <li>Der neue Auftrag wird geöffnet.</li>
                            </ul>
                            <AngebotPositionenSelect
                                angebotPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (angebot?.nr && kunde.data) {
                            await addAuftragFromAngebot(kunde.data, angebot, transferPositionen || []).then(
                                (result) => {
                                    history.push(createRecordUrl('auftraege', angebot.kNr, result.newNr));
                                },
                            );
                        }
                    }}
                    onCancel={() => {
                        setShowCreateAuftrag(false);
                        // setTransferKundeNr(null);
                        // setTransferKunde(null);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {showCreateService && (
                <ConfirmMessage
                    title="Service erstellen"
                    message={'Aus dem Angebot ' + angebot?.nr + ' wird ein Service erstellt.'}
                    minWidth={1000}
                    confirmText="Service erstellen"
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>Eine Service mit den gewählten Positionen und Mengen wird erstellt.</li>
                                <li>Die vorgeschlagenen Mengen wurden vom Angebot übernommen.</li>
                                <li>Doppelte Artikel wurden zusammengefasst.</li>
                                <li>Das neue Service wird geöffnet.</li>
                            </ul>
                            <AngebotPositionenSelect
                                angebotPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt={false}
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (angebot?.nr) {
                            await addServiceFromAngebot(artikelCalculator, angebot, transferPositionen || []).then(
                                (result) => {
                                    onClose(true);
                                    history.push(createRecordUrl('services', angebot.kNr, result.newNr));
                                },
                            );
                        }
                    }}
                    onCancel={() => {
                        setShowCreateService(false);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    content={
                        <div className={changeStatusValue === 3 ? styles.confirmContainer : ''}>
                            Soll der Status von Angebot {angebot?.nr} auf &quot;{AngebotStatusLabel[changeStatusValue]}
                            &quot; geändert werden?
                            {changeStatusValue === 3 && (
                                <div className={styles.ablehnGrund}>
                                    <AblehnGrund
                                        value={ablehnGrund.toString() || '0'}
                                        onChange={(newValue): void => {
                                            setAblehnGrund(parseInt(newValue, 10));
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    }
                    onConfirm={() => {
                        if (angebot?.nr) {
                            save(
                                () => setChangeStatusValue(null),
                                changeStatusValue,
                                changeStatusValue === 3
                                    ? {
                                          ablehnGrund,
                                      }
                                    : undefined,
                            );
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && aNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.angebot}
                    forcedSmRef={aNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch().then();
                        }
                    }}
                />
            )}
        </>
    );
};
